import { useNavigate } from "react-router-dom";
import { useEvent } from "../context/EventProvider";
import { useAccessLog } from "../log/actionLog";
import { useFlag, useText } from "../settings/context";
import { SiteTiles, useEntry } from "../pages/nav";
import { useReaderSignedIn } from "../panel/mode";
import { Button } from "../ui/Button";
import { withContext } from "./registry";
import { fallbackOf } from "../settings/catalog";
import { Prose } from "../ui/Prose";

/**
 * THE HOME SCREEN.
 *
 * Nothing here is hard-coded: title, subtitle and button all come from the
 * cascade (language × typology). Switching language or typology in the panel
 * therefore changes what this screen renders, with no reload.
 *
 * `data-cfg` marks the configurable areas: hovering a setting highlights them.
 */
export function Home() {
  /**
   * ⚠️ AUCUN REPLI SUR CE TITRE, et c'est demandé : vide veut dire vide. Il portait
   * « Bienvenue » en dur, donc un organisateur qui effaçait le champ voyait
   * « Bienvenue » revenir et n'avait aucun moyen de s'en débarrasser. Le mot
   * d'accueil est à lui, y compris le choix de ne pas en avoir.
   */
  const written = useText("FO_MEETING_V2_HOME_TITLE");
  const writtenSubtitle = useText("FO_MEETING_V2_HOME_SUBTITLE");
  /* Le bloc libre de la V1 (`FO_MEETING_HOME`, rendu en tête de son accueil). Sans
     lui, la reprise n'avait nulle part où poser ce que l'organisateur y avait écrit. */
  const html = useText("FO_MEETING_V2_HOME_HTML");
  const ctaVisible = useFlag("FO_MEETING_V2_HOME_CTA");
  const writtenCta = useText(
    "FO_MEETING_V2_HOME_CTA_TEXT",
    fallbackOf("FO_MEETING_V2_HOME_CTA_TEXT"),
  );

  /**
   * ═══ CE QUE LIT QUELQU'UN QUI N'EST PAS ENCORE DES VÔTRES ═══
   *
   * « Continuer » ne veut rien dire pour qui n'a rien commencé, et le mot d'accueil
   * d'un participant inscrit sonne faux devant un visiteur qui découvre l'événement.
   * Trois textes de rechange, servis à qui n'est pas connecté.
   *
   * ⚠️ VIDES, ILS RENDENT LA MAIN AUX PREMIERS, et c'est ce qui permet d'ajouter la
   * famille sans changer un seul accueil existant : ne rien écrire, c'est servir ce
   * qu'on servait. Écrire, c'est différencier.
   *
   * ⚠️ LA BASCULE SUIT LE LECTEUR, PAS L'INTERRUPTEUR « PUBLIC » DE LA PAGE. Un
   * organisateur en aperçu « non connecté » doit lire ces textes-là, sinon il règle à
   * l'aveugle ; et quelqu'un renvoyé sur l'accueil depuis une section ouverte est bien
   * un visiteur, quel que soit l'état de l'interrupteur.
   */
  const reader = useReaderSignedIn();
  const publicTitle = useText("FO_MEETING_V2_HOME_PUBLIC_TITLE");
  const publicSubtitle = useText("FO_MEETING_V2_HOME_PUBLIC_SUBTITLE");
  const publicHtml = useText("FO_MEETING_V2_HOME_PUBLIC_HTML");
  const publicCta = useText("FO_MEETING_V2_HOME_PUBLIC_CTA_TEXT");
  const instead = (own: string, ordinary: string) =>
    !reader && own.trim() !== "" ? own : ordinary;

  const title = instead(publicTitle, written);
  const subtitle = instead(publicSubtitle, writtenSubtitle);
  const shownHtml = instead(publicHtml, html);
  const ctaLabel = instead(publicCta, writtenCta);

  const navigate = useNavigate();
  const event = useEvent();
  // Seulement connecté : l'accueil public ne regarde personne.
  useAccessLog("homeAccess");

  /**
   * THE FIRST ACTIVE STEP OF THE FUNNEL — computed, not written here.
   *
   * Not merely the first DECLARED step: automatic registration takes the
   * participation step out of the thread, and this button must then open the one
   * after it. Hard-coded, it would keep serving a step the settings have removed —
   * the exact defect the prototype's review found on its own entry button.
   *
   * ⚠️ ET PAR LA CONNEXION QUAND PERSONNE N'EST CONNECTÉ, comme la tuile et l'entrée
   * de menu qui visent le même endroit : voir `useEntry`. Sans quoi, sur un site
   * public, ce bouton renvoyait à l'accueil d'où on venait de cliquer.
   */
  const entry = useEntry();

  return (
    <section className="page">
      {/* ⚠️ `hidden` PLUTÔT QU'UN RENDU CONDITIONNEL — la leçon du bandeau. Un nœud
          absent emporte la cible de survol de l'organisateur (`data-cfg`) : la ligne
          « Titre » du panneau n'éclairerait plus rien, et il ne saurait pas où son
          réglage agit. Masqué, le bloc ne prend aucune place (le garde-fou pose
          `[hidden] { display: none !important }`) et reste pointable. */}
      <div
        className="hero"
        data-cfg="homeHero"
        hidden={!title.trim() && !subtitle.trim()}
      >
        {/* Chacun disparaît pour son compte : un sous-titre sans titre est un choix
            possible, et un `<h2>` vide laissait sa hauteur de ligne derrière lui. */}
        {title.trim() !== "" && <h2>{title}</h2>}
        {subtitle.trim() !== "" && <p>{subtitle}</p>}
      </div>

      {/* Le bloc libre, rendu tel que l'organisateur l'a écrit — la même confiance
          que le produit lui accorde déjà sur ses pages d'information. */}
      {shownHtml.trim() !== "" && (
        <Prose
          html={shownHtml}
          className="se-prose"
          data-cfg="homeHero"
          />
      )}

      {/* ⚠️ LE BOUTON D'ABORD, LES TUILES ENSUITE — l'ordre du prototype, et il a une
          raison : l'appel à l'action est ce que le visiteur est venu faire, les
          tuiles sont ce qu'il peut faire ensuite. Mises au-dessus, elles repoussaient
          l'inscription sous une grille qui grandit à chaque page activée. */}
      <Button
        data-cfg="homeCta"
        hidden={!ctaVisible}
        onClick={() =>
          entry &&
          navigate(withContext(entry.path, event?.token), {
            state: entry.state,
          })
        }
      >
        {ctaLabel}
      </Button>

      <SiteTiles />
    </section>
  );
}
